import { registerRoutes } from '../utils/router.js';

/**
 * Route discovery mechanism.
 *
 * This file uses Vite's `import.meta.glob` to lazily load all route-related
 * files from the `../routes` directory when initRoutes() is called.
 * It then processes them into a structured and queryable format and registers them with the router.
 */

/**
 * Initialize the routes system by loading all route modules
 * @returns {Promise<void>}
 */
export async function initRoutes() {
  // Lazy import all `.js` files within the routes directory.
  const modules = import.meta.glob('../routes/**/*.js');
  
  // Load all modules
  const loadedModules = await Promise.all(
    Object.entries(modules).map(async ([path, importFn]) => {
      const module = await importFn();
      return [path, module];
    })
  );
  
  const routeModules = {};
  loadedModules.forEach(([path, module]) => {
    // Exclude this index file from the routes
    if (path !== '../routes/index.js') {
      routeModules[path] = module;
    }
  });
  
  const processedRoutes = Object.entries(routeModules).map(([path, module]) => {
    const fileName = path.split('/').pop();
    const slot = fileName.replace('.js', '');

    // Derive the subpath inside the routes directory
    const routesMarker = '/routes/';
    const idx = path.lastIndexOf(routesMarker);
    let rel = idx >= 0 ? path.slice(idx + routesMarker.length) : path;

    // Strip any leading './' fragments that can appear in Vite keys
    rel = rel.replace(/^(\.\/)+/, '');

    // Remove the filename at the end whether or not it has a preceding slash
    if (rel.endsWith(fileName)) {
      rel = rel.slice(0, rel.length - fileName.length);
    }

    // Normalize '/./' segments and duplicate slashes
    rel = rel
      .replace(/(^|\/)\.(?=\/|$)/g, '')
      .replace(/\/{2,}/g, '/')
      .replace(/\/$/, '');

    // Compute the final route path
    let routePath = rel ? '/' + rel.replace(/^\/+/, '') : '/';

    // All routes use prefix matching - they match their path and all child paths
    // This enables hierarchical composition without special filename conventions
    // e.g., /app matches /app, /app/projects, /app/project/1, /app/project/1/info, etc.
    const regexPattern = `^${routePath.replace(/\[([^\]]+)\]/g, '([^/]+)')}(/.*)?$`;
    const regex = new RegExp(regexPattern);
    
    // Extract param names from the path
    const params = (routePath.match(/\[([^\]]+)\]/g) || []).map(p => p.slice(1, -1));

    return {
      path: routePath, // The original file-based path
      slot,           // The name of the slot it fills
      regex,          // The regex for matching URLs
      params,         // The names of the dynamic parameters
      module,         // The actual imported module
      depth: routePath.split('/').filter(Boolean).length, // Add depth for sorting
    };
  });

  // Register the processed routes with the router utility
  registerRoutes(processedRoutes);
  
  console.log('Routes initialized');
}
